import { memo, useEffect } from 'react';
import * as yup from 'yup';
import { t } from 'i18next';
import { useFormik } from 'formik';
import { Dialog, DialogContent, DialogTitle, Grid } from '@mui/material';

import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormSwitchField from 'components/ui/form/FormSwitchField';
import FormTextField from 'components/ui/form/FormTextField';

import useFormSubmit from 'hooks/useFormSubmit';

const validationSchema = yup.object({
  name: yup.string().min(3).required('settings.crmSms.validation.name'),
  text: yup.string().required('settings.crmSms.validation.text'),
});

export default memo(function ModalAddEdit({
  open,
  setOpen,
  refetch,
  selected,
  setSelected,
}) {
  const { submit, isSubmitting } = useFormSubmit();

  const handleClose = () => {
    setOpen(false);
    setSelected({});
  };
  const handleFinishRequest = () => {
    refetch();
    handleClose();
  };

  const formik = useFormik({
    initialValues: {
      name: null,
      text: null,
      status: '1',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (selected?.id) {
        submit(
          { type: 'postUpdate', contentType: 'simple' },
          values,
          '/admin/crmsms',
          values?.name,
          selected?.id,
          false,
          handleFinishRequest
        );
      } else
        submit(
          { type: 'post', contentType: 'simple' },
          values,
          '/admin/crmsms',
          values?.name,
          null,
          false,
          handleFinishRequest
        );
    },
  });
  const { setValues } = formik;

  useEffect(() => {
    if (selected?.name && selected?.id) {
      setValues(selected);
    }

    return () => {
      // cleanup
    };
  }, [selected, setValues]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      maxWidth='lg'
      PaperProps={{ className: '!base-border !shadow-none' }}
      disableEscapeKeyDown={true}
    >
      <DialogTitle
        id='alert-dialog-title'
        className='!py-3 text-center !bg-gray-100 !mb-4 !min-w-[620px]'
      >
        <span className='pr-5 text-base'>
          {selected?.id
            ? t('settings.crmSms.modal.editTitle', { value: selected?.name })
            : t('settings.crmSms.modal.addTitle')}
        </span>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Grid
            container
            spacing={{ xs: 2, sm: 3, lg: 3 }}
            rowSpacing={1}
            columns={{ xs: 12, sm: 12, lg: 12 }}
          >
            <Grid item={true} lg={9} sm={9} xs={12}>
              <FormTextField
                delay={0}
                duration={0}
                fieldName='name'
                label={t('common.fields.simpleName')}
                formik={formik}
              />
            </Grid>
            <Grid item={true} lg={3} sm={3} xs={12}>
              <FormSwitchField
                delay={0}
                duration={0}
                label={t('common.fields.status')}
                fieldName='status'
                options={[
                  {
                    value: '1',
                    checked: true,
                  },
                  {
                    value: '0',
                    checked: false,
                  },
                ]}
                formik={formik}
              />
            </Grid>
            <Grid item={true} lg={12} sm={12} xs={12}>
              <FormTextField
                delay={0}
                duration={0}
                multiline={true}
                minRows={4}
                fieldName='text'
                label={t('common.fields.text')}
                formik={formik}
              />
            </Grid>

            <Grid item={true} sm={12} xs={12}>
              <FormActionButtons
                delay={0}
                duration={0}
                isSubmitting={isSubmitting}
                formType='dialog'
                setOpen={setOpen}
                reset={formik.resetForm}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
});
